import { useLazyQuery } from '@apollo/client'
import { useMemo, useCallback, useState, useRef, useEffect } from 'react'
import { useAuth } from '../../../context/auth-context'
import { COMPANY_SEARCH } from '../../../graphql/company/Queries'
import { CompanyScope, CompanySearchQuery, CompanySearchQueryVariables } from '../../../types'

export const COMPANY_SEARCH_LIMIT = 10

export const useCompanySearch = (
  companyScope: CompanyScope,
): {
  searchTerm: string
  setSearchTerm: (value: string) => void
  handleSearch: (value: string) => void
  handleClear: () => void
  companies: CompanySearchQuery['companySearch']
  loading: boolean
  shouldShowOptions: boolean
} => {
  const { user } = useAuth()
  const locale = user.locale.replace('_', '-')
  const [searchTerm, setSearchTerm] = useState('')
  const [companies, setCompanies] = useState<CompanySearchQuery['companySearch']>([])
  const [isSearching, setIsSearching] = useState(false)
  const timer = useRef<number>()

  const [getCompanies, { data, loading }] = useLazyQuery<CompanySearchQuery, CompanySearchQueryVariables>(COMPANY_SEARCH, {
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (data?.companySearch && !loading) {
      setCompanies(data.companySearch)
      setIsSearching(false)
    }
  }, [data, loading])

  useEffect(() => {
    setCompanies([])
  }, [searchTerm])

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim()
      setSearchTerm(trimmedValue)

      // Clear existing timer
      if (timer.current) {
        clearTimeout(timer.current)
      }

      // Set loading state when starting search
      if (trimmedValue.length > 1) {
        setIsSearching(true)
      }

      // Set new timer for debounced search
      timer.current = window.setTimeout(() => {
        // Only search if there's at least one keyword with 2+ characters
        const atLeastOneKeywordWithTwoOrMoreLetters = trimmedValue.split(' ').some((keyword) => keyword.length > 1)

        if (atLeastOneKeywordWithTwoOrMoreLetters) {
          getCompanies({
            variables: {
              companyScope: companyScope,
              search: trimmedValue,
              limit: COMPANY_SEARCH_LIMIT,
            },
          })
        } else {
          setIsSearching(false)
        }
      }, 700)
    },
    [getCompanies, companyScope],
  )

  const handleClear = useCallback(() => {
    setSearchTerm('')
    setCompanies([])
    setIsSearching(false)
    if (timer.current) {
      clearTimeout(timer.current)
    }
  }, [])

  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id, locale)),
    [companies, locale],
  )

  const shouldShowOptions = useMemo(() => {
    return searchTerm.length > 1 && sortedCompanies.length > 0
  }, [searchTerm, sortedCompanies])

  return {
    searchTerm,
    setSearchTerm,
    handleSearch,
    handleClear,
    companies: sortedCompanies,
    loading: loading || isSearching,
    shouldShowOptions,
  }
}
